// frontend/src/pages/AssetReceipt.js
/**
 * Public (no-login) asset-receipt acknowledgement page.
 *
 * Reached by outsourced asset holders (e.g. JB office) via a one-time link
 * emailed to their work address: `/asset-receipt?t=<token>`. There is NO
 * authentication — the signed token in the URL is the only credential.
 *
 * Flow:
 *   1. On load, GET /public/asset-receipt/?t=<token> -> holder + pending assets.
 *   2. Holder reviews the list and clicks "Confirm receipt".
 *   3. POST /public/asset-receipt/ {token} -> acknowledges; backend then emails
 *      the PDF acknowledgement letter to their work email.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, AlertCircle, Package, Loader2, ShieldCheck } from 'lucide-react';

// Standalone axios for unauthenticated calls (mirrors AttendEvent.js).
const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api/v1',
  timeout: 20000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

const Centered = ({ children }) => (
  <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
    <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg p-6 sm:p-8">{children}</div>
  </div>
);

const AssetReceipt = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || searchParams.get('token') || '';

  const [phase, setPhase] = useState('loading'); // loading | review | submitting | done | error
  const [errorMsg, setErrorMsg] = useState('');
  const [holderName, setHolderName] = useState('');
  const [assets, setAssets] = useState([]);
  const [acknowledgedCount, setAcknowledgedCount] = useState(0);

  const loadDetails = useCallback(async () => {
    if (!token) {
      setPhase('error');
      setErrorMsg('This link is missing its token. Please use the link from your email.');
      return;
    }
    setPhase('loading');
    try {
      const { data } = await publicApi.get('/public/asset-receipt/', { params: { t: token } });
      if (data.already_acknowledged) {
        setAcknowledgedCount(0);
        setPhase('done');
        return;
      }
      setHolderName(data.holder_name || '');
      setAssets(data.assets || []);
      setPhase('review');
    } catch (err) {
      setPhase('error');
      setErrorMsg(
        err?.response?.data?.detail ||
          'This link is invalid or has expired. Please contact your IT custodian.'
      );
    }
  }, [token]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  const handleConfirm = async () => {
    setPhase('submitting');
    try {
      const { data } = await publicApi.post('/public/asset-receipt/', { token });
      setAcknowledgedCount(data.acknowledged_count || 0);
      setPhase('done');
    } catch (err) {
      setPhase('error');
      setErrorMsg(
        err?.response?.data?.detail ||
          'We could not confirm your receipt. Please try again or contact your IT custodian.'
      );
    }
  };

  // ── Loading ───────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <Centered>
        <div className="text-center py-10">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your asset details…</p>
        </div>
      </Centered>
    );
  }

  // ── Error ─────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <Centered>
        <div className="text-center py-6">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-red-600" />
          </div>
          <h1 className="text-lg font-bold text-gray-900 mb-2">Link unavailable</h1>
          <p className="text-sm text-gray-600">{errorMsg}</p>
        </div>
      </Centered>
    );
  }

  // ── Done ──────────────────────────────────────────────────
  if (phase === 'done') {
    return (
      <Centered>
        <div className="text-center py-6">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-7 h-7 text-green-600" />
          </div>
          <h1 className="text-lg font-bold text-gray-900 mb-2">Receipt confirmed</h1>
          <p className="text-sm text-gray-600">
            {acknowledgedCount > 0
              ? `Thank you. You've confirmed receipt of ${acknowledgedCount} asset${acknowledgedCount === 1 ? '' : 's'}.`
              : 'These assets have already been acknowledged.'}
          </p>
          <p className="text-sm text-gray-500 mt-3">
            A copy of your acknowledgement letter will be emailed to you shortly. You may close this page.
          </p>
        </div>
      </Centered>
    );
  }

  // ── Review / confirm ──────────────────────────────────────
  return (
    <Centered>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
          <ShieldCheck className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-gray-900">Confirm asset receipt</h1>
          {holderName && <p className="text-sm text-gray-500">Hi {holderName}</p>}
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        The following {assets.length === 1 ? 'asset has' : 'assets have'} been assigned to you.
        Please confirm that you have received {assets.length === 1 ? 'it' : 'them'}.
      </p>

      <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 mb-6">
        {assets.map((a) => (
          <div key={a.transfer_id} className="flex items-start gap-3 p-3">
            <Package className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">
                {a.asset_name}{' '}
                <span className="font-mono text-xs text-gray-500">({a.asset_tag})</span>
              </p>
              {a.serial_number && (
                <p className="text-xs text-gray-500">S/N: {a.serial_number}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={handleConfirm}
        disabled={phase === 'submitting'}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl py-3 flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
      >
        {phase === 'submitting' ? (
          <><Loader2 className="w-4 h-4 animate-spin" />Confirming…</>
        ) : (
          <><CheckCircle2 className="w-4 h-4" />Confirm receipt</>
        )}
      </button>

      <p className="text-xs text-gray-400 text-center mt-4">
        This is a secure, single-use link. Please do not share it.
      </p>
    </Centered>
  );
};

export default AssetReceipt;
